import * as aeSDK from "@soluto-private/mx-ae-sdk";
import {
  addEventListener,
  attachSDKToContainer,
  EventName,
  hide as sdkHide,
  initialize,
  overrideExpertise as sdkOverrideExpertise,
  sendHiddenCustomerMessage,
  show,
  triggerMessagingOverlay,
} from "@soluto-private/mx-ae-sdk";
import {
  AuthenticationState,
  getAuthenticationState,
} from "@soluto-private/mx-app-authentication";
import { trackEvent, updateStickyProps } from "@trailhead/analytics";

export class AEClient {
  private static async _init(partner: string): Promise<void> {
    if (
      getAuthenticationState().toString() === AuthenticationState.LoggedIn &&
      !process.env.E2E_ENV
    ) {
      await initialize(partner);
    } else {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      await aeSDK.initializeAnonymous(partner); // eslint-disable-line @typescript-eslint/no-unsafe-call
    }
  }

  private static _setupListeners(): void {
    addEventListener(EventName.LoginFailed, args =>
      trackEvent("ExpertChatSessionMessageSendLogOutFailed", { ...args }),
    );
    addEventListener(EventName.LoggedOut, () =>
      trackEvent("ExpertChatSessionLoggedOut"),
    );
    addEventListener(EventName.LoggedIn, args => {
      trackEvent("UserLoggedInToExpertChat", { homeUserId: args.homeUserId });
      updateStickyProps({ homeUserId: args.homeUserId });
    });

    addEventListener(EventName.SessionChanged, args => {
      trackEvent("ExpertChatSessionChanged", {
        expertChatSessionAction: args.action,
        expertChatSessionExpertise: args.expertise,
        expertChatSessionPool: args.pool,
        expertChatSessionId: args.sessionId,
        expertChatExpertId: args.expertId,
      });
      updateStickyProps({
        expertChatSessionAction: args.action,
        expertChatSessionExpertise: args.expertise,
        expertChatSessionPool: args.pool,
        expertChatSessionId: args.sessionId,
        expertChatExpertId: args.expertId,
      });
    });

    addEventListener(EventName.SendMessageFailed, args => {
      trackEvent("ExpertChatSessionMessageSendFailed", {
        ...args,
      });
    });
    addEventListener(EventName.MessageSent, args => {
      trackEvent("ExpertChatSessionMessageSent", {
        messageType: args.type,
        expertChatSessionId: args.sessionId,
        wasHiddenMessage: args.message === "I need some help",
      });
    });
    addEventListener(EventName.MessageReceived, args => {
      trackEvent("ExpertChatSessionMessageReceived", {
        messageType: args.type,
        message: args.message,
        from: args.from,
      });
    });

    addEventListener(EventName.FabioClicked, args =>
      trackEvent("ExpertChatBubbleClicked", { isOpen: args.isExpanded }),
    );
  }

  static async init(
    partner: string,
    expertise: string[],
    onLoadCompleteCallback: () => void,
  ): Promise<void> {
    if (typeof partner === "undefined") {
      return;
    }

    this._setupListeners();
    await this.overrideExpertise(expertise);
    await this._init(partner);
    try {
      await this.showMessaging();
      onLoadCompleteCallback();
    } catch (e) {
      onLoadCompleteCallback();
    }
  }

  static async showMessaging(): Promise<void> {
    if (!this.sdkAvailable()) {
      return;
    }
    await show();
  }

  static async sendHiddenMessage(message: string): Promise<void> {
    await sendHiddenCustomerMessage(message);
  }

  static async attachToContainer(containerId: string): Promise<void> {
    await attachSDKToContainer(containerId);
  }

  static async hideMessaging(): Promise<void> {
    await sdkHide();
  }

  private static sdkAvailable(): boolean {
    return (window as unknown as { AE_SDK: null }).AE_SDK != null;
  }

  static async openMessagingOverlay(message?: string): Promise<void> {
    await triggerMessagingOverlay(message);
  }

  static async overrideExpertise(expertise: string[]): Promise<void> {
    await sdkOverrideExpertise(expertise);
  }
}

export type AEClientType = typeof AEClient;
